import { useForm } from "@mantine/form";
import { TARGET_COUNTRIES } from "../_Shared/targetMarkets";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Progress,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Loader,
  Center,
  Divider,
  ActionIcon,
  Avatar,
  TagsInput,
  Alert,
} from "@mantine/core";
import { IconCheck, IconX, IconEdit } from "@tabler/icons-react";
import { useBrandsStore } from "./brandsStore";
import { detectBrands } from "./brandsApi";
import BrandLogo from "../_Shared/BrandLogo";

export default function DetectBrands({
  modalOpened,
  setModalOpened,
  initialValues,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createBrand } = useBrandsStore();
  const [detectedBrands, setDetectedBrands] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentBrandIndex, setCurrentBrandIndex] = useState(0);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [editingBrand, setEditingBrand] = useState(null);

  const { t } = useTranslation("brands", {
    keyPrefix: "detectModal",
  });

  const steps = [
    "detect", // Step 0: URL input and detection
    ...detectedBrands.map((_, index) => `brand-${index}`), // Steps 1-N: Review each brand
    "summary", // Final step: Summary and save
  ];

  const form = useForm({
    initialValues: {
      url: initialValues?.url || "",
    },
    validate: {
      url: (value) => {
        if (!value.trim())
          return t("validation.urlRequired", "URL is required");
        try {
          new URL(value);
          return null;
        } catch {
          return t("validation.urlInvalid", "Please enter a valid URL");
        }
      },
    },
  });

  const brandForm = useForm({
    initialValues: {
      name: "",
      description: "",
      descriptionNative: "",
      industries: [],
      logoUrl: "",
      defaultTargetCountryCode: "US",
      defaultTargetLanguageCode: "en",
    },
  });

  useEffect(() => {
    if (modalOpened) {
      setCurrentStep(0);
      setDetectedBrands([]);
      setSelectedBrands([]);
      setCurrentBrandIndex(0);
      setEditingBrand(null);
      form.reset();
    }
  }, [modalOpened]);

  const handleDetection = (values) => {
    setIsSubmitting(true);

    detectBrands({ WebsiteUrl: values.url })
      .then((brands) => {
        if (brands.length === 0) {
          notifications.show({
            title: t("notifications.noBrandsFoundTitle", "No brands found"),
            message: t(
              "notifications.noBrandsFoundMessage",
              "No brands were detected on this website"
            ),
            color: "yellow",
          });
          return;
        }

        setDetectedBrands(brands);
        setSelectedBrands(brands.map(() => true)); // Initially select all brands
        setCurrentStep(1);
        setCurrentBrandIndex(0);
      })
      .catch((error) => {
        console.error("Failed to detect brands:", error);
        notifications.show({
          title: t("notifications.detectErrorTitle", "Detection failed"),
          message: t(
            "notifications.detectErrorMessage",
            "Failed to detect brands from the website"
          ),
          color: "red",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleBrandToggle = (index, selected) => {
    const newSelected = [...selectedBrands];
    newSelected[index] = selected;
    setSelectedBrands(newSelected);
  };

  const handleBrandEdit = (index, brandData) => {
    const newBrands = [...detectedBrands];
    newBrands[index] = { ...newBrands[index], ...brandData };
    setDetectedBrands(newBrands);
    setEditingBrand(null);
  };

  const handleNextStep = () => {
    if (currentStep === 0) {
      form.onSubmit(handleDetection)();
    } else if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      if (currentStep < detectedBrands.length) {
        setCurrentBrandIndex(currentStep);
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      if (currentStep <= detectedBrands.length) {
        setCurrentBrandIndex(currentStep - 2);
      }
    }
  };

  const handleFinalSave = () => {
    setIsSubmitting(true);

    const brandsToSave = detectedBrands
      .filter((_, index) => selectedBrands[index])
      .map((brand) => ({
        name: brand.brandName,
        description: brand.description,
        descriptionNative: brand.descriptionNative,
        industries: brand.industries,
        logoUrl: brand.logoUrl,
        websiteUrl: form.values.url,
        defaultTargetCountryCode: brand.defaultTargetCountryCode || "US",
        defaultTargetLanguageCode: brand.defaultTargetLanguageCode || "en",
      }));

    const savePromises = brandsToSave.map((brandData) =>
      createBrand(brandData)
    );

    Promise.all(savePromises)
      .then((savedBrands) => {
        notifications.show({
          title: t("notifications.saveSuccessTitle", "Brands saved"),
          message: t("notifications.saveSuccessMessage", {
            count: savedBrands.length,
            defaultValue: `${savedBrands.length} brand(s) saved successfully`,
          }),
          color: "green",
        });
        setModalOpened(false);
        form.reset();
      })
      .catch((error) => {
        console.error("Failed to save brands:", error);
        notifications.show({
          title: t("notifications.saveErrorTitle", "Save failed"),
          message: t("notifications.saveErrorMessage", "Failed to save brands"),
          color: "red",
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const getCurrentBrand = () => {
    if (currentStep === 0 || currentStep >= detectedBrands.length + 1)
      return null;
    return detectedBrands[currentStep - 1];
  };

  const getProgressValue = () => {
    return ((currentStep + 1) / steps.length) * 100;
  };

  const renderDetectionStep = () => (
    <Stack>
      Just enter the URL of the website you want to detect brands from. Our AI
      will analyze the page and find brands mentioned on it!
      <TextInput
        label={t("labels.url", "Website URL (including http(s)://)")}
        placeholder={t("placeholders.url", "https://www.example.com")}
        required
        {...form.getInputProps("url")}
      />
    </Stack>
  );

  const renderBrandStep = () => {
    const brand = getCurrentBrand();
    if (!brand) return null;

    const isSelected = selectedBrands[currentStep - 1];
    const isEditing = editingBrand === currentStep - 1;

    if (isEditing) {
      return (
        <Stack>
          <Group justify="space-between">
            <Title order={4}>{t("editBrand", "Edit Brand")}</Title>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => setEditingBrand(null)}
            >
              {t("buttons.cancel", "Cancel")}
            </Button>
          </Group>

          <TextInput
            label={t("labels.name", "Brand Name")}
            value={brandForm.values.name}
            onChange={(e) => brandForm.setFieldValue("name", e.target.value)}
          />

          <Textarea
            label={t("labels.description", "Description")}
            value={brandForm.values.description}
            onChange={(e) =>
              brandForm.setFieldValue("description", e.target.value)
            }
            minRows={2}
          />

          <Textarea
            label={t("labels.descriptionNative", "Native Description")}
            value={brandForm.values.descriptionNative}
            onChange={(e) =>
              brandForm.setFieldValue("descriptionNative", e.target.value)
            }
            minRows={2}
          />

          <TagsInput
            label={t("labels.industries", "Industries")}
            {...brandForm.getInputProps("industries")}
          />

          <TextInput
            label={t("labels.logoUrl", "Logo URL")}
            {...brandForm.getInputProps("logoUrl")}
          />

          <Group grow>
            <Select
              searchable
              label={t("labels.country", "Default Country")}
              {...brandForm.getInputProps("defaultTargetCountryCode")}
              data={TARGET_COUNTRIES}
            />

            <Select
              searchable
              label={t("labels.language", "Default Language")}
              {...brandForm.getInputProps("defaultTargetLanguageCode")}
              data={[
                { value: "en", label: "English" },
                { value: "de", label: "Deutsch" },
                { value: "fr", label: "Français" },
                { value: "es", label: "Español" },
                { value: "it", label: "Italiano" },
                { value: "pt", label: "Português" },
                { value: "ar", label: "العربية" },
                { value: "zh", label: "中文" },
                { value: "ja", label: "日本語" },
                { value: "ko", label: "한국어" },
                { value: "ru", label: "Русский" },
                { value: "hi", label: "हिन्दी" },
                { value: "bn", label: "বাংলা" },
                { value: "pa", label: "ਪੰਜਾਬੀ" },
                { value: "ur", label: "اردو" },
                { value: "vi", label: "Tiếng Việt" },
                { value: "th", label: "ไทย" },
                { value: "id", label: "Bahasa Indonesia" },
                { value: "tr", label: "Türkçe" },
                { value: "fil", label: "Filipino" },
                { value: "ms", label: "Bahasa Melayu" },
                { value: "sw", label: "Kiswahili" },
                { value: "fa", label: "فارسی" },
                { value: "he", label: "עברית" },
                { value: "uk", label: "Українська" },
                { value: "cs", label: "Čeština" },
                { value: "pl", label: "Polski" },
                { value: "ro", label: "Română" },
                { value: "hu", label: "Magyar" },
                { value: "nl", label: "Nederlands" },
                { value: "sv", label: "Svenska" },
                { value: "fi", label: "Suomi" },
                { value: "da", label: "Dansk" },
                { value: "no", label: "Norsk" },
                { value: "el", label: "Ελληνικά" },
                { value: "bg", label: "Български" },
              ]}
            />
          </Group>

          <Group>
            <Select
              label={t("labels.geographicScope", "Geographic Scope")}
              placeholder={t(
                "placeholders.selectGeographicScope",
                "Select geographic scope"
              )}
              data={["Local", "National", "Global"]}
              {...brandForm.getInputProps("geographicScope")}
            />
          </Group>

          <Group justify="flex-end">
            <Button
              onClick={() => {
                handleBrandEdit(currentStep - 1, brandForm.values);
              }}
            >
              {t("buttons.save", "Save Changes")}
            </Button>
          </Group>
        </Stack>
      );
    }

    return (
      <Stack>
        <Group justify="space-between">
          <Title order={4}>
            {t("reviewBrand", "Review Brand")} {currentStep}/
            {detectedBrands.length}
          </Title>
          <Badge
            color={isSelected ? "green" : "gray"}
            variant={isSelected ? "filled" : "outline"}
          >
            {isSelected
              ? t("status.selected", "Selected")
              : t("status.skipped", "Skipped")}
          </Badge>
        </Group>

        <Card withBorder>
          <Group>
            {/* {brand.logoUrl && (
              <Avatar
                src={brand.logoUrl}
                alt={brand.brandName}
                size="lg"
                radius="sm"
              />
            )} */}
            <BrandLogo brand={brand} size={32} />
            <div style={{ flex: 1 }}>
              <Group>
                <Title order={5}>{brand.brandName}</Title>
                {brand.prominence && (
                  <Badge size="xs" variant="outline">
                    {t("prominence", "Prominence")}:{" "}
                    {(brand.prominence * 100).toFixed(0)}%
                  </Badge>
                )}
              </Group>
              {brand.industries?.map((industry, index) => (
                <Badge key={index} size="sm" variant="light" mr={5}>
                  {industry}
                </Badge>
              ))}
            </div>
            <Group>
              <ActionIcon
                variant="subtle"
                onClick={() => {
                  brandForm.setValues({
                    name: brand.brandName,
                    description: brand.description || "",
                    descriptionNative: brand.descriptionNative || "",
                    industries: brand.industries || [],
                    logoUrl: brand.logoUrl || "",
                    defaultTargetCountryCode:
                      brand.defaultTargetCountryCode || "US",
                    defaultTargetLanguageCode:
                      brand.defaultTargetLanguageCode || "en",
                  });
                  setEditingBrand(currentStep - 1);
                }}
              >
                <IconEdit size={16} />
              </ActionIcon>
            </Group>
          </Group>

          <Stack mt="md">
            {brand.description && (
              <div>
                <Text size="sm" fw={500}>
                  {t("labels.description", "Description")}
                </Text>
                <Text size="sm">{brand.description}</Text>
              </div>
            )}

            {brand.descriptionNative &&
              brand.descriptionNative !== brand.description && (
                <div>
                  <Text size="sm" fw={500}>
                    {t("labels.nativeDescription", "Native Description")}
                  </Text>
                  <Text size="sm">{brand.descriptionNative}</Text>
                </div>
              )}

            <Group>
              <Text size="sm" fw={500}>
                {t("labels.country", "Country")}:
              </Text>
              <Text size="sm">{brand.defaultTargetCountryCode || "US"}</Text>
              <Text size="sm" fw={500}>
                {t("labels.language", "Language")}:
              </Text>
              <Text size="sm">{brand.defaultTargetLanguageCode || "en"}</Text>
            </Group>
          </Stack>
        </Card>

        <Group justify="center" mt="md">
          <Button
            variant={!isSelected ? "filled" : "outline"}
            color="red"
            onClick={() => handleBrandToggle(currentStep - 1, false)}
            leftSection={<IconX size={16} />}
          >
            {t("buttons.skip", "Skip this brand")}
          </Button>
          <Button
            variant={isSelected ? "filled" : "outline"}
            color="green"
            onClick={() => handleBrandToggle(currentStep - 1, true)}
            leftSection={<IconCheck size={16} />}
          >
            {t("buttons.select", "Select this brand")}
          </Button>
        </Group>
      </Stack>
    );
  };

  const renderSummaryStep = () => {
    const selectedCount = selectedBrands.filter(Boolean).length;
    const selectedBrandsList = detectedBrands.filter(
      (_, index) => selectedBrands[index]
    );

    return (
      <Stack>
        <Title order={4}>{t("summary", "Summary")}</Title>

        <Text>
          {t("summaryText", {
            count: selectedCount,
            total: detectedBrands.length,
            defaultValue: `You have selected ${selectedCount} out of ${detectedBrands.length} detected brands.`,
          })}
        </Text>

        {selectedCount > 0 && (
          <Stack mt="md">
            <Text fw={500}>{t("selectedBrands", "Selected brands")}:</Text>
            {selectedBrandsList.map((brand, index) => (
              <Card key={index} withBorder shadow="md" p="sm">
                <Group>
                  {brand.logoUrl && (
                    <Avatar src={brand.logoUrl} size="sm" radius="sm" />
                  )}
                  <div>
                    <Text size="sm" fw={500}>
                      {brand.brandName}
                    </Text>
                    <Group mt="xs" gap={4}>
                      {brand.industries?.map((industry, index) => (
                        <Badge
                          variant="outline"
                          key={index}
                          size="xs"
                          c="dimmed"
                        >
                          {industry}
                        </Badge>
                      ))}
                    </Group>
                  </div>
                </Group>
              </Card>
            ))}
          </Stack>
        )}

        {selectedCount === 0 && (
          <Text c="orange" mt="md">
            {t("noSelection", "No brands selected. No brands will be saved.")}
          </Text>
        )}
      </Stack>
    );
  };

  const renderCurrentStep = () => {
    if (isSubmitting && currentStep === 0) {
      return (
        <Center py="xl">
          <Stack align="center">
            <Loader size="lg" />
            <Text>{t("detecting", "Detecting brands...")}</Text>
          </Stack>
        </Center>
      );
    }

    switch (currentStep) {
      case 0:
        return renderDetectionStep();
      case steps.length - 1:
        return renderSummaryStep();
      default:
        return renderBrandStep();
    }
  };

  const getStepTitle = () => {
    if (currentStep === 0) return t("stepTitles.detect", "Enter Website URL");
    if (currentStep === steps.length - 1)
      return t("stepTitles.summary", "Review & Save");
    return t("stepTitles.review", "Review Brand");
  };

  const canGoNext = () => {
    if (currentStep === 0) return form.isValid() && !isSubmitting;
    if (currentStep === steps.length - 1) return false;
    return true;
  };

  const canGoPrev = () => {
    return currentStep > 0 && !isSubmitting;
  };

  const canSave = () => {
    return (
      currentStep === steps.length - 1 &&
      selectedBrands.some(Boolean) &&
      !isSubmitting
    );
  };

  return (
    <Modal
      opened={modalOpened}
      onClose={() => {
        setModalOpened(false);
        form.reset();
      }}
      title={
        <Stack gap="xs">
          <Title order={3}>{t("title", "Detect Brands")}</Title>
          <Progress value={getProgressValue()} size="sm" />
          <Text size="sm" c="dimmed">
            {getStepTitle()} ({currentStep + 1}/{steps.length})
          </Text>
        </Stack>
      }
      size="lg"
      centered
    >
      <Stack>
        {renderCurrentStep()}

        <Divider />

        <Group justify="space-between">
          <Button
            variant="default"
            onClick={
              canGoPrev()
                ? handlePrevStep
                : () => {
                    setModalOpened(false);
                    form.reset();
                  }
            }
            disabled={isSubmitting}
          >
            {canGoPrev()
              ? t("buttons.previous", "Previous")
              : t("buttons.cancel", "Cancel")}
          </Button>

          <Group>
            {canGoNext() && (
              <Button
                onClick={handleNextStep}
                disabled={!canGoNext()}
                loading={isSubmitting && currentStep === 0}
              >
                {currentStep === 0
                  ? t("buttons.detect", "Detect Brands")
                  : t("buttons.next", "Next")}
              </Button>
            )}

            {canSave() && (
              <Button
                onClick={handleFinalSave}
                disabled={!canSave()}
                loading={isSubmitting}
                color="green"
              >
                {t("buttons.saveBrands", "Save Selected Brands")}
              </Button>
            )}
          </Group>
        </Group>

        {isSubmitting && (
          <Alert title="Creating Brands" color="blue" radius="md">
            <Group>
              <Loader size="sm" />
              Gathering more details about the brand, just a sec!
            </Group>
          </Alert>
        )}
      </Stack>
    </Modal>
  );
}
